import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"
import { baseUrl, api_service_path } from "./utils"
import { getStudentToken } from "../../Lectures/session"
import { isOwner } from "../../utils/hooks"

/*
 * ---------------------------------------------------------------------------
 * Backend contract  (base: `${baseUrl}/api/lectures`)
 * ---------------------------------------------------------------------------
 * A "course" is what the catalogue sells. Courses belong to the platform, not
 * to an institution: every student, from any school, can buy any course.
 *   {
 *     course_id, title, description, subject_name, class_name (free text),
 *     price, currency, accent, instructor, created_at,
 *     lecture_count, is_enrolled          <- the last two are per-request
 *   }
 *
 * A "lecture" always belongs to exactly one course:
 *   {
 *     lecture_id, course_id, title, description, video_url, duration,
 *     subject_name, sequence, created_at
 *   }
 *
 * Student endpoints -- Authorization: Bearer <student token>
 *   POST   /student/register   { firstname, lastname, email, password,
 *                                institution_ref, class_id, roll_no }
 *                              -> { token, student }
 *                              class_id must be a class of institution_ref.
 *   POST   /student/login      { email, password } -> { token, student }
 *   GET    /student/courses    -> { list: [course] }  (every platform course)
 *   GET    /student/my-courses -> { list: [course] }          (enrolled only)
 *   GET    /student/course/:course_id
 *                              -> { course, is_enrolled, lectures: [lecture] }
 *                              When not enrolled the server MUST strip
 *                              `video_url` and set `locked: true` on each
 *                              lecture, so titles preview but nothing plays.
 *   POST   /student/course/:course_id/purchase { amount }
 *                              -> { enrollment, course }      FREE courses only
 *   POST   /student/course/:course_id/order
 *                              -> { key_id, order_id, amount, currency,
 *                                   course_title, prefill }   (Razorpay order)
 *   POST   /student/course/:course_id/verify
 *                              { razorpay_order_id, razorpay_payment_id,
 *                                razorpay_signature } -> { enrollment, course }
 *   GET    /student/lecture/:lecture_id -> { lecture }
 *                              403 unless the student owns the course.
 *
 * Owner endpoints -- Authorization: Bearer <staff token from current_user>
 * Every one is OWNER ONLY and answers 403 for any other user_type.
 *   GET    /manage/courses                   -> { list: [course] }
 *   POST   /manage/courses/create            { ...course } -> { course }
 *   PUT    /manage/courses/edit/:course_id   { ...course } -> { course }
 *   DELETE /manage/courses/remove/:course_id -> { detail }
 *   GET    /manage/course/:course_id/lectures -> { list: [lecture] }
 *   POST   /manage/create                     { ...lecture } -> { lecture }
 *   PUT    /manage/edit/:lecture_id           { ...lecture } -> { lecture }
 *   DELETE /manage/remove/:lecture_id         -> { detail }
 *
 * v2 -- chapters, resources, free previews, reviews and Q&A (all additive):
 *   course   += rating_average (1 decimal | null), rating_count,
 *               preview_count, chapter_count
 *   lecture  += chapter_id (null = ungrouped), is_preview, resource_count,
 *               resources: [resource]   <- only when unlocked for the caller
 *   chapter   = { chapter_id, course_id, title, description, sequence,
 *                 lecture_count, progress (student detail, enrolled only) }
 *   resource  = { resource_id, lecture_id, title, url, kind: file|link, ... }
 *   review    = { review_id, course_id, rating 1..5, comment, author_name,
 *                 is_mine, created_at, updated_at }
 *   question  = { question_id, lecture_id, course_id, title, body,
 *                 author_name, author_role, is_mine, answer_count,
 *                 has_instructor_answer, created_at, updated_at }
 *   answer    = { answer_id, question_id, body, author_name, author_role,
 *                 is_mine, created_at }
 *   A course's lectures are always ordered chapter by chapter, with the
 *   ungrouped ones last. A preview lecture plays without enrolment.
 *
 *   Student:
 *   GET    /student/course/:course_id  += chapters: [chapter]
 *   GET    /student/course/:course_id/reviews?offset&limit
 *                              -> { list, total, rating_average, rating_count,
 *                                   distribution: {"1".."5"}, my_review }
 *   PUT    /student/course/:course_id/review { rating, comment } (enrolled)
 *   DELETE /student/course/:course_id/review
 *   GET    /student/lecture/:lecture_id/questions?offset&limit
 *                              -> { list, total }      (enrolled only)
 *   POST   /student/lecture/:lecture_id/questions { title, body }
 *   GET    /student/question/:question_id -> { question, answers }
 *   POST   /student/question/:question_id/answers { body }
 *   DELETE /student/question/:question_id | /student/answer/:answer_id  (own)
 *
 *   Owner:
 *   GET    /manage/course/:course_id/chapters -> { list: [chapter] }
 *   POST   /manage/chapters/create { course_id, title, description }
 *   PUT    /manage/chapters/edit/:chapter_id { title, description }
 *   DELETE /manage/chapters/remove/:chapter_id  (409 while it has lectures)
 *   PUT    /manage/course/:course_id/chapters/reorder { chapter_ids }
 *   PUT    /manage/course/:course_id/lectures/reorder
 *                              { groups: [{ chapter_id|null, lecture_ids }] }
 *                              every lecture exactly once; also moves them
 *   POST   /manage/lecture/:lecture_id/resources { title, url, kind }
 *   DELETE /manage/resources/remove/:resource_id
 *   GET    /manage/course/:course_id/reviews?offset&limit
 *   DELETE /manage/reviews/remove/:review_id
 *   GET    /manage/questions?course_id&unanswered&offset&limit
 *   GET    /manage/question/:question_id -> { question, answers }
 *   POST   /manage/question/:question_id/answers { body }
 *   DELETE /manage/question/:question_id | /manage/answer/:answer_id
 *
 * ACCESS: every purchase unlocks a course for one year. `expires_at` is sent
 * with each enrolled course; once it passes the course leaves My Courses and
 * its lectures lock again until the student buys it again.
 *
 * PAYMENTS: paid courses go through Razorpay -- `order` creates the order that
 * Checkout opens with, and `verify` unlocks the course once the server has
 * checked Razorpay's signature. A webhook covers students who close the
 * browser mid-payment. See src/Lectures/usePurchaseFlow.js.
 * ---------------------------------------------------------------------------
 */

const lectureBaseUrl = `${baseUrl}/${api_service_path.lectures}`

/**
 * FastAPI reports validation failures (422) as a list of objects in `detail`.
 * Screens show `error.data.detail` as text, so flatten it to one message.
 */
const makeBaseQuery = (prepareHeaders) => {
    const baseQuery = fetchBaseQuery({ baseUrl: lectureBaseUrl, prepareHeaders })
    return async (args, api, extraOptions) => {
        const result = await baseQuery(args, api, extraOptions)
        const detail = result.error?.data?.detail
        if (Array.isArray(detail)) {
            result.error.data.detail =
                detail.map((item) => item?.msg).filter(Boolean).join(", ") ||
                "Please check the details and try again."
        }
        return result
    }
}

/**
 * Paged list params. `undefined` values are dropped by fetchBaseQuery, so
 * optional filters can be passed through as they are.
 */
const pageParams = ({ offset = 0, limit = 20, ...rest } = {}) => ({
    offset,
    limit,
    ...rest
})

/**
 * Student-facing API. Authenticates with the student session, which is stored
 * apart from the staff session -- see src/Lectures/session.js.
 */
export const studentLearnAction = createApi({
    reducerPath: "studentLearnApi",
    baseQuery: makeBaseQuery((headers) => {
        const token = getStudentToken()
        if (token) {
            headers.set("Authorization", `Bearer ${token}`)
        }
        return headers
    }),
    tagTypes: [
        "catalog",
        "myCourses",
        "course",
        "reviews",
        "questions",
        "question"
    ],
    endpoints: (builder) => ({
        studentRegister: builder.mutation({
            query: ({
                firstname,
                lastname,
                email,
                password,
                institution_ref,
                class_id,
                roll_no
            }) => ({
                url: "/student/register",
                method: "POST",
                body: {
                    firstname,
                    lastname,
                    email,
                    password,
                    institution_ref,
                    class_id,
                    roll_no
                }
            })
        }),
        studentLogin: builder.mutation({
            query: ({ email, password }) => ({
                url: "/student/login",
                method: "POST",
                body: { email, password }
            })
        }),
        getCourseCatalog: builder.query({
            query: () => ({ url: "/student/courses", method: "GET" }),
            providesTags: ["catalog"]
        }),
        getMyCourses: builder.query({
            query: () => ({ url: "/student/my-courses", method: "GET" }),
            providesTags: ["myCourses"]
        }),
        getCourseDetail: builder.query({
            query: ({ course_id }) => ({
                url: `/student/course/${course_id}`,
                method: "GET"
            }),
            providesTags: ["course"]
        }),
        purchaseCourse: builder.mutation({
            query: ({ course_id, amount }) => ({
                url: `/student/course/${course_id}/purchase`,
                method: "POST",
                body: { amount }
            }),
            /* A purchase changes the catalogue, My Courses and the detail. */
            invalidatesTags: ["catalog", "myCourses", "course"]
        }),
        createCourseOrder: builder.mutation({
            query: ({ course_id }) => ({
                url: `/student/course/${course_id}/order`,
                method: "POST"
            })
        }),
        verifyCoursePayment: builder.mutation({
            query: ({ course_id, ...body }) => ({
                url: `/student/course/${course_id}/verify`,
                method: "POST",
                body
            }),
            invalidatesTags: ["catalog", "myCourses", "course"]
        }),
        getLectureById: builder.query({
            query: ({ lecture_id }) => ({
                url: `/student/lecture/${lecture_id}`,
                method: "GET"
            }),
            providesTags: ["course"]
        }),

        /* ----------------------------- reviews ----------------------------- */

        getCourseReviews: builder.query({
            query: ({ course_id, ...paging }) => ({
                url: `/student/course/${course_id}/reviews`,
                method: "GET",
                params: pageParams(paging)
            }),
            providesTags: ["reviews"]
        }),
        saveMyReview: builder.mutation({
            query: ({ course_id, rating, comment }) => ({
                url: `/student/course/${course_id}/review`,
                method: "PUT",
                body: { rating, comment }
            }),
            /* The course JSON carries rating_average / rating_count too. */
            invalidatesTags: ["reviews", "catalog", "myCourses", "course"]
        }),
        deleteMyReview: builder.mutation({
            query: ({ course_id }) => ({
                url: `/student/course/${course_id}/review`,
                method: "DELETE"
            }),
            invalidatesTags: ["reviews", "catalog", "myCourses", "course"]
        }),

        /* ------------------------------- Q&A ------------------------------- */

        getLectureQuestions: builder.query({
            query: ({ lecture_id, ...paging }) => ({
                url: `/student/lecture/${lecture_id}/questions`,
                method: "GET",
                params: pageParams(paging)
            }),
            providesTags: ["questions"]
        }),
        askLectureQuestion: builder.mutation({
            query: ({ lecture_id, title, body }) => ({
                url: `/student/lecture/${lecture_id}/questions`,
                method: "POST",
                body: { title, body }
            }),
            invalidatesTags: ["questions"]
        }),
        getQuestionThread: builder.query({
            query: ({ question_id }) => ({
                url: `/student/question/${question_id}`,
                method: "GET"
            }),
            providesTags: ["question"]
        }),
        answerQuestion: builder.mutation({
            query: ({ question_id, body }) => ({
                url: `/student/question/${question_id}/answers`,
                method: "POST",
                body: { body }
            }),
            /* The list shows answer_count, so refresh it as well. */
            invalidatesTags: ["question", "questions"]
        }),
        deleteMyQuestion: builder.mutation({
            query: ({ question_id }) => ({
                url: `/student/question/${question_id}`,
                method: "DELETE"
            }),
            invalidatesTags: ["questions"]
        }),
        deleteMyAnswer: builder.mutation({
            query: ({ answer_id }) => ({
                url: `/student/answer/${answer_id}`,
                method: "DELETE"
            }),
            invalidatesTags: ["question", "questions"]
        })
    })
})

/**
 * Courses are platform content, so managing them is reserved for the owner.
 * The server is the authority, but non-owners are refused here too so the
 * request never leaves the browser.
 */
const ownerOnly = (request) => async (arg, api, extraOptions, baseQuery) => {
    if (!isOwner()) {
        return {
            error: {
                status: 403,
                data: { detail: "Only the owner can manage courses and lectures." }
            }
        }
    }
    return baseQuery(request(arg))
}

/**
 * Owner-facing API for curating the platform's courses and their lectures. Uses the same
 * `current_user` bearer token as every other authenticated slice.
 */
export const lectureAction = createApi({
    reducerPath: "lectureApi",
    baseQuery: makeBaseQuery((headers) => {
        const current_user = localStorage.getItem("current_user")
            ? JSON.parse(localStorage.getItem("current_user"))
            : null
        if (current_user?.token) {
            headers.set("Authorization", `Bearer ${current_user.token}`)
        }
        return headers
    }),
    tagTypes: ["course", "lecture", "chapter", "review", "question"],
    endpoints: (builder) => ({
        getCourses: builder.query({
            queryFn: ownerOnly(() => ({
                url: "/manage/courses",
                method: "GET"
            })),
            providesTags: ["course"]
        }),
        createCourse: builder.mutation({
            queryFn: ownerOnly((body) => ({
                url: "/manage/courses/create",
                method: "POST",
                body
            })),
            invalidatesTags: ["course"]
        }),
        editCourse: builder.mutation({
            queryFn: ownerOnly(({ course_id, ...body }) => ({
                url: `/manage/courses/edit/${course_id}`,
                method: "PUT",
                body
            })),
            invalidatesTags: ["course"]
        }),
        deleteCourse: builder.mutation({
            queryFn: ownerOnly(({ course_id }) => ({
                url: `/manage/courses/remove/${course_id}`,
                method: "DELETE"
            })),
            invalidatesTags: ["course", "lecture"]
        }),
        getCourseLectures: builder.query({
            queryFn: ownerOnly(({ course_id }) => ({
                url: `/manage/course/${course_id}/lectures`,
                method: "GET"
            })),
            providesTags: ["lecture"]
        }),
        createLecture: builder.mutation({
            queryFn: ownerOnly((body) => ({
                url: "/manage/create",
                method: "POST",
                body
            })),
            invalidatesTags: ["lecture", "course", "chapter"]
        }),
        editLecture: builder.mutation({
            queryFn: ownerOnly(({ lecture_id, ...body }) => ({
                url: `/manage/edit/${lecture_id}`,
                method: "PUT",
                body
            })),
            /* A lecture can change chapter or become a preview. */
            invalidatesTags: ["lecture", "course", "chapter"]
        }),
        deleteLecture: builder.mutation({
            queryFn: ownerOnly(({ lecture_id }) => ({
                url: `/manage/remove/${lecture_id}`,
                method: "DELETE"
            })),
            invalidatesTags: ["lecture", "course", "chapter"]
        }),

        /* ----------------------------- chapters ---------------------------- */

        getCourseChapters: builder.query({
            queryFn: ownerOnly(({ course_id }) => ({
                url: `/manage/course/${course_id}/chapters`,
                method: "GET"
            })),
            providesTags: ["chapter"]
        }),
        createChapter: builder.mutation({
            queryFn: ownerOnly(({ course_id, title, description }) => ({
                url: "/manage/chapters/create",
                method: "POST",
                body: { course_id, title, description }
            })),
            invalidatesTags: ["chapter", "course"]
        }),
        editChapter: builder.mutation({
            queryFn: ownerOnly(({ chapter_id, title, description }) => ({
                url: `/manage/chapters/edit/${chapter_id}`,
                method: "PUT",
                body: { title, description }
            })),
            invalidatesTags: ["chapter"]
        }),
        deleteChapter: builder.mutation({
            queryFn: ownerOnly(({ chapter_id }) => ({
                url: `/manage/chapters/remove/${chapter_id}`,
                method: "DELETE"
            })),
            invalidatesTags: ["chapter", "course"]
        }),
        reorderChapters: builder.mutation({
            queryFn: ownerOnly(({ course_id, chapter_ids }) => ({
                url: `/manage/course/${course_id}/chapters/reorder`,
                method: "PUT",
                body: { chapter_ids }
            })),
            invalidatesTags: ["chapter"]
        }),
        /*
         * `groups` must hold every lecture of the course exactly once; the
         * server rewrites chapter_id and sequence from it, which is also how
         * a lecture moves to another chapter.
         */
        reorderLectures: builder.mutation({
            queryFn: ownerOnly(({ course_id, groups }) => ({
                url: `/manage/course/${course_id}/lectures/reorder`,
                method: "PUT",
                body: { groups }
            })),
            invalidatesTags: ["lecture", "chapter"]
        }),

        /* ---------------------------- resources ---------------------------- */

        addLectureResource: builder.mutation({
            queryFn: ownerOnly(({ lecture_id, title, url, kind }) => ({
                url: `/manage/lecture/${lecture_id}/resources`,
                method: "POST",
                body: { title, url, kind }
            })),
            invalidatesTags: ["lecture"]
        }),
        deleteLectureResource: builder.mutation({
            queryFn: ownerOnly(({ resource_id }) => ({
                url: `/manage/resources/remove/${resource_id}`,
                method: "DELETE"
            })),
            invalidatesTags: ["lecture"]
        }),

        /* ----------------------------- reviews ----------------------------- */

        getManageCourseReviews: builder.query({
            queryFn: ownerOnly(({ course_id, ...paging }) => ({
                url: `/manage/course/${course_id}/reviews`,
                method: "GET",
                params: pageParams(paging)
            })),
            providesTags: ["review"]
        }),
        deleteReview: builder.mutation({
            queryFn: ownerOnly(({ review_id }) => ({
                url: `/manage/reviews/remove/${review_id}`,
                method: "DELETE"
            })),
            invalidatesTags: ["review", "course"]
        }),

        /* --------------------------- Q&A inbox ----------------------------- */

        getManageQuestions: builder.query({
            queryFn: ownerOnly(({ course_id, unanswered, ...paging } = {}) => ({
                url: "/manage/questions",
                method: "GET",
                params: pageParams({
                    ...paging,
                    course_id: course_id || undefined,
                    unanswered: unanswered ? true : undefined
                })
            })),
            providesTags: ["question"]
        }),
        getManageQuestionThread: builder.query({
            queryFn: ownerOnly(({ question_id }) => ({
                url: `/manage/question/${question_id}`,
                method: "GET"
            })),
            providesTags: ["question"]
        }),
        answerManageQuestion: builder.mutation({
            queryFn: ownerOnly(({ question_id, body }) => ({
                url: `/manage/question/${question_id}/answers`,
                method: "POST",
                body: { body }
            })),
            invalidatesTags: ["question"]
        }),
        deleteManageQuestion: builder.mutation({
            queryFn: ownerOnly(({ question_id }) => ({
                url: `/manage/question/${question_id}`,
                method: "DELETE"
            })),
            invalidatesTags: ["question"]
        }),
        deleteManageAnswer: builder.mutation({
            queryFn: ownerOnly(({ answer_id }) => ({
                url: `/manage/answer/${answer_id}`,
                method: "DELETE"
            })),
            invalidatesTags: ["question"]
        })
    })
})

export const {
    useStudentRegisterMutation,
    useStudentLoginMutation,
    useGetCourseCatalogQuery,
    useGetMyCoursesQuery,
    useGetCourseDetailQuery,
    usePurchaseCourseMutation,
    useCreateCourseOrderMutation,
    useVerifyCoursePaymentMutation,
    useGetLectureByIdQuery,
    useGetCourseReviewsQuery,
    useSaveMyReviewMutation,
    useDeleteMyReviewMutation,
    useGetLectureQuestionsQuery,
    useAskLectureQuestionMutation,
    useGetQuestionThreadQuery,
    useAnswerQuestionMutation,
    useDeleteMyQuestionMutation,
    useDeleteMyAnswerMutation
} = studentLearnAction

export const {
    useGetCoursesQuery,
    useLazyGetCoursesQuery,
    useCreateCourseMutation,
    useEditCourseMutation,
    useDeleteCourseMutation,
    useGetCourseLecturesQuery,
    useLazyGetCourseLecturesQuery,
    useCreateLectureMutation,
    useEditLectureMutation,
    useDeleteLectureMutation,
    useGetCourseChaptersQuery,
    useCreateChapterMutation,
    useEditChapterMutation,
    useDeleteChapterMutation,
    useReorderChaptersMutation,
    useReorderLecturesMutation,
    useAddLectureResourceMutation,
    useDeleteLectureResourceMutation,
    useGetManageCourseReviewsQuery,
    useDeleteReviewMutation,
    useGetManageQuestionsQuery,
    useGetManageQuestionThreadQuery,
    useAnswerManageQuestionMutation,
    useDeleteManageQuestionMutation,
    useDeleteManageAnswerMutation
} = lectureAction
